<template>
  <div class="contact-page">
    <div class="container contact-page__container">
      <HcBreadcrumb wrapper-class="contact-page__breadcrumb">
        <li class="breadcrumb-item"><a href="/">Home</a></li>
        <li class="breadcrumb-item active" aria-current="page">Contact us</li>
      </HcBreadcrumb>

      <div class="contact-layout hp-animate">
        <aside class="contact-info">
          <div class="contact-info__intro">
            <p class="contact-info__eyebrow">Get in touch</p>
            <h1 class="contact-info__title">We’re here to help</h1>
            <p class="contact-info__text">
              Questions about jobs, your profile, or hiring on Hireclap? Send us a message and our team will get back to you.
            </p>
          </div>

          <div class="contact-info__cards">
            <div class="contact-info-card">
              <div class="contact-info-card__icon" aria-hidden="true">
                <v-icon size="20">{{ $icons.emailOutline }}</v-icon>
              </div>
              <div class="contact-info-card__body">
                <p class="contact-info-card__label">Email</p>
                <p class="contact-info-card__value">
                  <a href="mailto:recruit@hireclap.com">recruit@hireclap.com</a>
                </p>
              </div>
            </div>

            <div class="contact-info-card">
              <div class="contact-info-card__icon" aria-hidden="true">
                <v-icon size="20">{{ $icons.deskphone }}</v-icon>
              </div>
              <div class="contact-info-card__body">
                <p class="contact-info-card__label">Phone</p>
                <p class="contact-info-card__value">
                  <a href="tel:+919037156256">+91 9037 156 256</a>
                </p>
                <p class="contact-info__hours">Mon–Sat, 9:30 AM – 6:30 PM IST</p>
              </div>
            </div>

            <div class="contact-info-card">
              <div class="contact-info-card__icon" aria-hidden="true">
                <v-icon size="20">{{ $icons.mapMarker }}</v-icon>
              </div>
              <div class="contact-info-card__body">
                <p class="contact-info-card__label">Office</p>
                <p class="contact-info-card__value">
                  21st A Cross Rd, Sector 3, HSR Layout,<br />
                  Bengaluru, Karnataka 560102, India
                </p>
              </div>
            </div>
          </div>
        </aside>

        <div class="contact-card">
          <header class="contact-card__header">
            <h2 class="contact-card__title">Send a message</h2>
            <p class="contact-card__subtitle">Fill in the form and we’ll respond as soon as possible.</p>
          </header>

          <div v-if="sentFlag" class="contact-alert contact-alert--success" role="status">
            <p class="contact-alert__title">Your enquiry has been sent successfully. We’ll get back to you soon.</p>
          </div>

          <p v-if="logged_in && !sentFlag" class="contact-card__logged-note">
            Signed in as {{ contact.name }} — your details are pre-filled from your profile.
          </p>

          <v-form ref="form" v-model="valid" lazy-validation class="contact-form">
            <div class="contact-form__field">
              <v-text-field
                type="text"
                id="name"
                v-model="contact.name"
                :disabled="logged_in"
                :rules="[v => !!v || 'Name is required']"
                outlined
                dense
                hide-details="auto"
              >
                <template #label>Name<span class="red--text"><strong>*</strong></span></template>
              </v-text-field>
            </div>

            <div class="contact-form__field">
              <v-text-field
                type="email"
                id="email"
                v-model="contact.email"
                :disabled="logged_in"
                :rules="emailRules"
                outlined
                dense
                hide-details="auto"
              >
                <template #label>Email<span class="red--text"><strong>*</strong></span></template>
              </v-text-field>
            </div>

            <div class="contact-form__field contact-form__phone">
              <span class="contact-form__prefix">+91</span>
              <v-text-field
                type="tel"
                v-model="contact.mobile"
                :disabled="logged_in"
                :rules="mobileRules"
                maxlength="10"
                outlined
                dense
                hide-details="auto"
              >
                <template #label>Mobile<span class="red--text"><strong>*</strong></span></template>
              </v-text-field>
            </div>

            <div class="contact-form__field">
              <v-textarea
                id="message"
                v-model="contact.comment"
                :rules="[v => !!v || 'Message is required']"
                rows="4"
                outlined
                dense
                hide-details="auto"
                auto-grow
              >
                <template #label>Message<span class="red--text"><strong>*</strong></span></template>
              </v-textarea>
            </div>

            <div class="contact-form__submit">
              <button
                type="button"
                class="hp-btn hp-btn--primary"
                :disabled="submitting"
                @click="submitContact"
              >
                {{ submitting ? 'Sending…' : 'Send message' }}
              </button>
            </div>
          </v-form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Actions from '../libraries/apiActions.js';
import HcBreadcrumb from '@/components/chrome/HcBreadcrumb.vue';

import '@/assets/home-page.css';
import '@/assets/contact-page.css';

const breadcrumbJson = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [{
    '@type': 'ListItem',
    position: 1,
    name: 'Home',
    item: window.location.origin,
  }, {
    '@type': 'ListItem',
    position: 2,
    name: 'Contact Us',
    item: window.location.href,
  }],
};

export default {
  name: 'contact_us',
  components: { HcBreadcrumb },
  metaInfo: {
    title: 'Contact us | Hireclap',
    link: [
      { rel: 'canonical', href: `${process.env.VUE_APP_URL || ''}/contact-us` },
    ],
    script: [
      { type: 'application/ld+json', json: breadcrumbJson },
    ],
  },
  data() {
    return {
      action: new Actions(),
      valid: false,
      logged_in: false,
      submitting: false,
      contact: {
        name: null,
        email: null,
        mobile: null,
        comment: null,
        src: null,
        uid: null,
      },
      sentFlag: false,
      emailRules: [
        v => !!v || 'Email is required',
        v => /^(([^<>()[\]\\.,;:\s@']+(\.[^<>()\\[\]\\.,;:\s@']+)*)|('.+'))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(v) || 'Enter a valid email',
      ],
      mobileRules: [
        v => !!v || 'Mobile number is required',
        v => !/\D/g.test(v) || 'Enter a valid mobile number',
        v => (v && v.length === 10) || 'Enter a 10-digit mobile number',
      ],
    };
  },
  async beforeMount() {
    if (this.$cookies.get('hire-seeker')) {
      await this.getUser(this.$cookies.get('hire-seeker').token);
    }
  },
  methods: {
    async getUser(token) {
      await this.action.getUser(token).then((data) => {
        if (data) {
          this.logged_in = true;
          this.contact.name = data.user.name;
          this.contact.email = data.user.email;
          this.contact.mobile = data.user.mobile;
          this.contact.uid = data.user.id;
        }
      }).catch(() => {});
    },
    submitContact() {
      this.valid = this.$refs.form.validate();
      if (!this.valid) return;

      this.submitting = true;
      if (this.$route.query.src) this.contact.src = this.$route.query.src;

      this.action.sendContact(this.contact).then((res) => {
        if (res.status === 1) {
          this.sentFlag = true;
          this.cancel();
        }
      }).finally(() => {
        this.submitting = false;
      });
    },
    cancel() {
      if (!this.logged_in) {
        this.contact.name = null;
        this.contact.email = null;
        this.contact.mobile = null;
      }
      this.valid = false;
      this.contact.comment = null;
      this.$refs.form.resetValidation();
    },
  },
};
</script>
